import React, { useState, useCallback, useEffect } from 'react';
import { LoadingInline } from './LoadingOverlay';
import { generateSeatId, getRowLabel } from '../utils/seatNaming';
import { api } from '../services/api';
import { toast, confirm } from '../utils/swal';
import './TicketingSeatMap.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TicketingSeatMap = ({
  venue, event, invitationCode, onBack, onCheckout, onGoHome,
  // SignalR props — App.js'den geliyor
  seats, mySeats, selectSeat, releaseSeat, confirmSeat,
  isConnected, minSecondsLeft,
  selectedSeatsWithTypes, setSelectedSeatsWithTypes,
}) => {
  const [blockedSeatsMap, setBlockedSeatsMap] = useState({}); // { seatId: 'protocol'|'guest' }

  // Dışarıdan gelen SeatReleased — sidebar'ı temizle
  useEffect(() => {
    // mySeats'te olmayan ama selectedSeatsWithTypes'ta olan koltukları temizle
    setSelectedSeatsWithTypes(prev => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach(seatId => {
        if (!mySeats.has(seatId)) {
          delete next[seatId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [mySeats]);

  useEffect(() => {
    api.getBlockedSeats(event.id)
      .then(data => {
        const map = {};
        (data || []).forEach(s => { map[s.seatId] = s.type; });
        setBlockedSeatsMap(map);
      })
      .catch(() => {});
  }, [event.id]);

  const [showTicketTypeModal, setShowTicketTypeModal] = useState(false);
  const [currentSeatId, setCurrentSeatId] = useState(null);
  const [currentPairSeatId, setCurrentPairSeatId] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  const [doubleClickMode, setDoubleClickMode] = useState(event.enableDoubleClickSelection || false);

  const ticketTypes = [
    { value: 0, label: 'Tam Bilet', icon: '👤', color: '#4CAF50' },
    { value: 1, label: 'Öğrenci', icon: '🎓', color: '#2196F3' }
  ];

  const findPairSeat = (seatId) => {
    if (!doubleClickMode) return null;

    const alphabetMatch = seatId.match(/^([A-Z]+)(\d+)$/);
    if (alphabetMatch) {
      const [, row, num] = alphabetMatch;
      const seatNum = parseInt(num);
      
      let pairSeat;
      
      if (seatNum % 2 === 1) {
        pairSeat = `${row}${seatNum + 1}`;
      } else {
        pairSeat = `${row}${seatNum - 1}`;
      }
      
      const pairOccupiedByOthers = seats[pairSeat] && !mySeats.has(pairSeat);
      
      if (!pairOccupiedByOthers) {
        return pairSeat;
      }
      
      const otherSide = (seatNum % 2 === 1) 
        ? `${row}${seatNum - 1}`
        : `${row}${seatNum + 1}`;
      
      const otherOccupiedByOthers = seats[otherSide] && !mySeats.has(otherSide);
      
      if (!otherOccupiedByOthers && parseInt(otherSide.match(/\d+$/)?.[0]) > 0) {
        return otherSide;
      }
      
      return null;
    }
    
    const prefixMatch = seatId.match(/^(.+?)(\d+)-(\d+)$/);
    if (prefixMatch) {
      const [, prefix, rowNum, seatNum] = prefixMatch;
      const num = parseInt(seatNum);
      
      let pairSeat;
      
      if (num % 2 === 1) {
        pairSeat = `${prefix}${rowNum}-${num + 1}`;
      } else {
        pairSeat = `${prefix}${rowNum}-${num - 1}`;
      }
      
      const pairOccupiedByOthers = seats[pairSeat] && !mySeats.has(pairSeat);
      
      if (!pairOccupiedByOthers) {
        return pairSeat;
      }
      
      const otherSide = (num % 2 === 1) 
        ? `${prefix}${rowNum}-${num - 1}`
        : `${prefix}${rowNum}-${num + 1}`;
      
      const otherOccupiedByOthers = seats[otherSide] && !mySeats.has(otherSide);
      
      if (!otherOccupiedByOthers && parseInt(otherSide.match(/-(\d+)$/)?.[1]) > 0) {
        return otherSide;
      }
      
      return null;
    }

    return null;
  };

  const handleSeatClick = async (seatId) => {
    if (!isConnected) {
      toast.warning('Bağlantı kuruluyor, lütfen bekleyin...');
      return;
    }

    if (mySeats.has(seatId)) {
      if (doubleClickMode) {
        const pairSeat = findPairSeat(seatId);
        if (pairSeat && mySeats.has(pairSeat)) {
          await handleRemoveSeat(seatId);
          await handleRemoveSeat(pairSeat);
          return;
        }
      }
      await handleRemoveSeat(seatId);
      return;
    }

    const section = venue.sections.find(s => {
      if (s.namingMode === 0 || !s.namingMode) {
        const seatLetter = seatId.match(/^[A-Z]+/)?.[0];
        if (!seatLetter) return false;
        
        let seatRowNumber = 0;
        for (let i = 0; i < seatLetter.length; i++) {
          seatRowNumber = seatRowNumber * 26 + (seatLetter.charCodeAt(i) - 64);
        }
        
        return seatRowNumber >= s.startRow && seatRowNumber <= s.endRow;
      }
      
      return seatId.startsWith(s.rowPrefix);
    });

    if (!section) {
      console.error('❌ Bölüm bulunamadı:', seatId);
      toast.error('Koltuk bilgisi hatalı!');
      return;
    }

    const basePrice = event.sectionPricing?.[section.id] || 0;
    
    if (basePrice === 0) {
      toast.error('Bu bölüm için fiyat belirlenememiş!');
      return;
    }

    let seatsToSelect = [seatId];
    if (doubleClickMode) {
      const pairSeat = findPairSeat(seatId);
      if (pairSeat) {
        const pairOccupied = seats[pairSeat] && !mySeats.has(pairSeat);
        if (pairOccupied) {
          toast.warning(`Yan koltuk (${pairSeat}) dolu! Tek koltuk modu ile seçin.`);
          return;
        }
        seatsToSelect.push(pairSeat);
      } else {
        toast.info('Yan koltuk bulunamadı. Tek koltuk olarak seçiliyor.');
      }
    }

    // ── DAVET KODU LİMİT KONTROLÜ ──
    if (invitationCode?.maxTickets != null) {
      const currentCount = mySeats.size;
      const willAdd = seatsToSelect.length;
      if (currentCount + willAdd > invitationCode.maxTickets) {
        const remaining = invitationCode.maxTickets - currentCount;
        if (remaining <= 0) {
          toast.warning(`Bu davet kodu ile en fazla ${invitationCode.maxTickets} koltuk seçebilirsiniz.`);
        } else {
          toast.warning(`Bu davet kodu ile en fazla ${invitationCode.maxTickets} koltuk seçebilirsiniz. Yalnızca ${remaining} koltuk daha seçebilirsiniz.`);
        }
        return;
      }
    }

    const hasStudentDiscount = event.enableStudentDiscount && 
                              event.sectionStudentPricing?.[section.id] > 0;

    if (hasStudentDiscount) {
      setCurrentSeatId(seatId);
      setCurrentPairSeatId(seatsToSelect[1] || null);
      setCurrentSection(section);
      setShowTicketTypeModal(true);
    } else {
      for (const seat of seatsToSelect) {
        await selectSeatWithType(seat, section, 'Full');
      }
    }
  };

  const selectSeatWithType = async (seatId, section, ticketType) => {
    if (mySeats.has(seatId)) {
      console.log('⚠️ Koltuk zaten seçili, atlanıyor:', seatId);
      return;
    }

    const success = await selectSeat(seatId);
    if (!success) {
      toast.error(`${seatId} koltuğu başkası tarafından seçildi!`);
      return;
    }

    const basePrice = ticketType === 'Full' 
      ? event.sectionPricing[section.id]
      : event.sectionStudentPricing[section.id];

    try {
      const priceData = await api.calculatePrice({
        eventId: event.id,
        sectionId: section.id,
        basePrice: basePrice,
        ticketType: ticketType === 'Student' ? 1 : 0
      });

      setSelectedSeatsWithTypes(prev => ({
        ...prev,
        [seatId]: {
          seatId: seatId,
          ticketType: ticketType === 'Student' ? 1 : 0,
          sectionName: section.name,
          basePrice: basePrice,
          finalPrice: priceData?.finalPrice ?? basePrice
        }
      }));
    } catch (error) {
      console.error('Fiyat hesaplama hatası:', error);
      await releaseSeat(seatId);
      toast.error('Fiyat hesaplanamadı!');
    }
  };

  const handleTicketTypeSelect = async (ticketType) => {
    if (!currentSeatId || !currentSection) return;
    
    await selectSeatWithType(currentSeatId, currentSection, ticketType);
    
    if (currentPairSeatId) {
      await selectSeatWithType(currentPairSeatId, currentSection, ticketType);
    }
    
    setShowTicketTypeModal(false);
    setCurrentSeatId(null);
    setCurrentPairSeatId(null);
    setCurrentSection(null);
  };

  const handleRemoveSeat = async (seatId) => {
    await releaseSeat(seatId);
    // Functional update — stale closure sorunu olmaz
    setSelectedSeatsWithTypes(prev => {
      const next = { ...prev };
      delete next[seatId];
      return next;
    });
  };

  const getSeatStatus = (seatId, section) => {
    // Backend'den gelen bloklu koltuklar
    if (blockedSeatsMap[seatId] === 'protocol') return 'blocked-protocol';
    if (blockedSeatsMap[seatId] === 'guest')    return 'blocked-guest';
    // Eski disabledSeats kontrolü (geriye dönük)
    if (section?.disabledSeats?.includes(seatId)) return 'blocked';
    const seatInfo = seats[seatId];
    if (!seatInfo) return 'available';
    if (mySeats.has(seatId)) {
      const ticketType = selectedSeatsWithTypes[seatId]?.ticketType;
      if (ticketType === 0) return 'my-full';
      if (ticketType === 1) return 'my-student';
      return 'my-temp';
    }
    return 'occupied';
  };

  const getTotalPrice = () => {
    return Object.values(selectedSeatsWithTypes).reduce(
      (total, seat) => total + seat.finalPrice, 
      0
    );
  };

  const handleCheckout = () => {
    if (Object.keys(selectedSeatsWithTypes).length === 0) {
      toast.warning('Lütfen en az bir koltuk seçin!');
      return;
    }

    onCheckout({
      event,
      venue,
      selectedSeats: selectedSeatsWithTypes,
      totalPrice: getTotalPrice()
    });
  };

  return (
    <div className="ticketing-seat-map">
      <div className="map-container">
        <div className="seats-section">
          <div className="header">
            <button onClick={onBack} className="back-btn">← Geri</button>
            <div className="header-event-info">
              {(event.mainPhoto || event.venueMainPhoto) && (
                <div className="header-event-img">
                  <img
                    src={`${API_URL}${event.mainPhoto || event.venueMainPhoto}`}
                    alt={event.name}
                  />
                </div>
              )}
              <div>
                <h2>{event.name}</h2>
                <p className="subtitle">
                  📍 {venue.name} &nbsp;·&nbsp; 📅 {new Date(event.date).toLocaleDateString('tr-TR', {day:'numeric',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                </p>
              </div>
            </div>
            <div className="header-right">
              <div className={`status ${isConnected ? 'connected' : 'disconnected'}`}>
                {isConnected
                  ? <><i className="fa-solid fa-circle" style={{color:'#22c55e',fontSize:'0.7em'}} /> Bağlı</>
                  : <><i className="fa-solid fa-circle" style={{color:'#ef4444',fontSize:'0.7em'}} /> Bağlantı Yok</>}
              </div>
              {onGoHome && (
                <button onClick={onGoHome} className="home-btn" title="Ana Sayfaya Dön">
                  <i className="fa-solid fa-house" /> Ana Sayfa
                </button>
              )}
            </div>
          </div>

          {event.enableDoubleClickSelection && (
            <div className="double-mode-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={doubleClickMode}
                  onChange={(e) => setDoubleClickMode(e.target.checked)}
                />
                <span className="toggle-text">
                  👥 Çift Koltuk Modu 
                  {doubleClickMode ? ' (Aktif)' : ' (Kapalı)'}
                </span>
              </label>
              <p className="toggle-hint">
                {doubleClickMode 
                  ? 'Her tıklamada 2 koltuk seçilecek' 
                  : 'Tek tek koltuk seçiyorsunuz'}
              </p>
            </div>
          )}

          <div className="legend">
            <div><span className="seat-legend available"></span> Boş</div>
            <div><span className="seat-legend my-temp"></span> Seçildi</div>
            <div><span className="seat-legend occupied"></span> Dolu</div>
            <div><span className="seat-legend blocked-protocol"></span> Protokol</div>
            <div><span className="seat-legend blocked-guest"></span> Davetli</div>
          </div>

          <div className="stage"><i className="fa-solid fa-clapperboard" /> SAHNE</div>

          {(() => {
            const hasPositions = venue.sections.some(s => s.position?.x !== undefined);

            if (hasPositions) {
              // Tasarımcıdaki Konva koordinat sistemi:
              // Bölüm görsel genişliği = seatsPerRow * 25
              // Bölüm görsel yüksekliği = rowCount * 20
              // Biz koltuk haritasında daha büyük render ediyoruz,
              // bu yüzden bir scale faktörü uyguluyoruz.

              // ── RENDER SABİTLERİ ──
              const SEAT_W = 30;
              const SEAT_H = 30;
              const ROW_LABEL_W = 36;
              const SECTION_PADDING = 12;
              const SECTION_HEADER_H = 52;
              const GAP = 16; // bölümler arası boşluk

              // Her bölümün gerçek render boyutu
              const getSectionRenderSize = (s) => {
                const rowCount = s.endRow - s.startRow + 1;
                const maxSeats = s.seatsPerRow;
                const w = ROW_LABEL_W + maxSeats * (SEAT_W + 4) + SECTION_PADDING * 2;
                const h = SECTION_HEADER_H + rowCount * (SEAT_H + 4) + SECTION_PADDING * 2;
                return { w, h };
              };

              // Tasarımcı koordinat sistemi: bölüm w = seatsPerRow * 25, h = rowCount * 20
              // Global scale: tasarımcıdaki toplam genişliği gerçek genişliğe oranla
              const designSectionW = (s) => s.seatsPerRow * 25;
              const designSectionH = (s) => (s.endRow - s.startRow + 1) * 20;

              // Her bölümün tasarımcıdaki sağ/alt ucunu hesapla
              const designMaxX = Math.max(...venue.sections.map(s =>
                (s.position?.x || 0) + designSectionW(s)
              ));
              const designMaxY = Math.max(...venue.sections.map(s =>
                (s.position?.y || 0) + designSectionH(s)
              ));

              // Gerçek render boyutlarından toplam alan
              // Bölümleri x pozisyonuna göre sırala ve gerçek boyutlarla yerleştir
              const sortedByX = [...venue.sections].sort((a, b) =>
                (a.position?.x || 0) - (b.position?.x || 0)
              );

              // ── YENİ ALGORİTMA: Göreli sıra korunarak yeniden dizleme ──
              // 1. Bölümleri tasarımcı Y pozisyonuna göre satırlara grupla
              // 2. Her satırı X pozisyonuna göre sırala
              // 3. Satırları gerçek render boyutlarıyla alt alta, bölümleri yan yana yerleştir

              const ROW_THRESHOLD = 50; // aynı satırda sayılmak için max Y farkı (px)

              // Bölümleri Y'e göre satırlara grupla
              const sorted = [...venue.sections].sort((a, b) =>
                (a.position?.y || 0) - (b.position?.y || 0)
              );

              const rows = []; // [[section, ...], [section, ...]]
              sorted.forEach(s => {
                const sy = s.position?.y || 0;
                const lastRow = rows[rows.length - 1];
                if (lastRow && Math.abs((lastRow[0].position?.y || 0) - sy) < ROW_THRESHOLD) {
                  lastRow.push(s);
                } else {
                  rows.push([s]);
                }
              });

              // Her satır içini X'e göre sırala
              rows.forEach(row => row.sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0)));

              // Her bölümün gerçek pozisyonunu hesapla
              const sectionPositions = new Map(); // sectionId -> {left, top}
              let currentTop = 0;

              rows.forEach(row => {
                let currentLeft = 0;
                const rowHeight = Math.max(...row.map(s => getSectionRenderSize(s).h));

                row.forEach(s => {
                  const { w } = getSectionRenderSize(s);
                  sectionPositions.set(s.id, { left: currentLeft, top: currentTop });
                  currentLeft += w + GAP;
                });

                currentTop += rowHeight + GAP;
              });

              const getScaledPos = (s) => sectionPositions.get(s.id) || { left: 0, top: 0 };

              // Canvas boyutu
              const positions = venue.sections.map(s => {
                const { left, top } = getScaledPos(s);
                const { w, h } = getSectionRenderSize(s);
                return { left, top, right: left + w, bottom: top + h };
              });

              const canvasW = Math.max(...positions.map(p => p.right)) + 40;
              const canvasH = Math.max(...positions.map(p => p.bottom)) + 40;

              return (
                <div className="venue-layout-2d-scroll">
                  <div
                    className="venue-layout-2d"
                    style={{ width: canvasW, height: canvasH }}
                  >
                    {venue.sections.map((section, idx) => {
                      const totalRows = section.endRow - section.startRow + 1;
                      const sectionPrice = event.sectionPricing?.[section.id] || 0;
                      const { left, top } = getScaledPos(section);

                      return (
                        <div
                          key={section.id}
                          className="section section-2d"
                          style={{ left, top }}
                        >
                          <div className="section-header">
                            <h3>{section.name}</h3>
                            <span className="section-price">₺{sectionPrice}</span>
                          </div>

                          <div className="seats-grid">
                            {Array.from({ length: totalRows }, (_, rowIndex) => {
                              const seatsInThisRow = section.rowSeats?.[rowIndex] || section.seatsPerRow;
                              return (
                                <div key={rowIndex} className="seat-row">
                                  <span className="row-label">{getRowLabel(section, rowIndex)}</span>
                                  {Array.from({ length: seatsInThisRow }, (_, seatIndex) => {
                                    const seatId = generateSeatId(section, rowIndex, seatIndex);
                                    const status = getSeatStatus(seatId, section);
                                    const isOccupied = (seats[seatId] && !mySeats.has(seatId)) || status === 'blocked' || status === 'blocked-protocol' || status === 'blocked-guest';
                                    return (
                                      <button
                                        key={seatIndex}
                                        className={`seat ${status}`}
                                        onClick={() => !['blocked','blocked-protocol','blocked-guest'].includes(status) && handleSeatClick(seatId)}
                                        disabled={isOccupied || !isConnected}
                                        title={status === 'blocked-protocol' ? `${seatId} - Protokol` : status === 'blocked-guest' ? `${seatId} - Davetli` : status === 'blocked' ? `${seatId} - Blokeli` : `${seatId} - ₺${sectionPrice}`}
                                      >
                                        {section.reverseSeats ? (section.seatStartNumber ?? 1) + seatsInThisRow - 1 - seatIndex : (section.seatStartNumber ?? 1) + seatIndex}
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // ── LİSTE DÜZENİ (fallback) ──
            return (
              <div className="venue-layout">
                {[...venue.sections]
                  .sort((a, b) => (a.position?.y || 999) - (b.position?.y || 999))
                  .map((section) => {
                    const totalRows = section.endRow - section.startRow + 1;
                    const sectionPrice = event.sectionPricing?.[section.id] || 0;
                    return (
                      <div key={section.id} className="section">
                        <div className="section-header">
                          <h3>{section.name}</h3>
                          <span className="section-price">₺{sectionPrice}</span>
                        </div>
                        <div className="seats-grid">
                          {Array.from({ length: totalRows }, (_, rowIndex) => {
                            const seatsInThisRow = section.rowSeats?.[rowIndex] || section.seatsPerRow;
                            return (
                              <div key={rowIndex} className="seat-row">
                                <span className="row-label">{getRowLabel(section, rowIndex)}</span>
                                {Array.from({ length: seatsInThisRow }, (_, seatIndex) => {
                                  const seatId = generateSeatId(section, rowIndex, seatIndex);
                                  const status = getSeatStatus(seatId, section);
                                  const isOccupied = (seats[seatId] && !mySeats.has(seatId)) || status === 'blocked' || status === 'blocked-protocol' || status === 'blocked-guest';
                                  return (
                                    <button
                                      key={seatIndex}
                                      className={`seat ${status}`}
                                      onClick={() => !['blocked','blocked-protocol','blocked-guest'].includes(status) && handleSeatClick(seatId)}
                                      disabled={isOccupied || !isConnected}
                                      title={status === 'blocked-protocol' ? `${seatId} - Protokol` : status === 'blocked-guest' ? `${seatId} - Davetli` : status === 'blocked' ? `${seatId} - Blokeli` : `${seatId} - ₺${sectionPrice}`}
                                    >
                                      {section.reverseSeats ? (section.seatStartNumber ?? 1) + seatsInThisRow - 1 - seatIndex : (section.seatStartNumber ?? 1) + seatIndex}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            );
          })()}
        </div>

        {Object.keys(selectedSeatsWithTypes).length > 0 && (
          <div className="sidebar-panel">
            <h3><i className="fa-solid fa-ticket" /> Seçili Biletler</h3>

            {/* 5 DAKİKA TIMER */}
            {minSecondsLeft !== null && (
              <div className={`seat-timer-bar ${minSecondsLeft < 60 ? 'urgent' : ''}`}>
                <i className="fa-solid fa-clock seat-timer-icon" />
                <span className="seat-timer-text">
                  {minSecondsLeft < 60 ? 'Süre doluyor!' : 'Rezerve tutuldu'}
                </span>
                <span className="seat-timer-countdown">
                  {Math.floor(minSecondsLeft / 60)}:{String(minSecondsLeft % 60).padStart(2, '0')}
                </span>
              </div>
            )}

            {invitationCode?.maxTickets != null && (
              <div className="invite-limit-info">
                <i className="fa-solid fa-ticket" /> Davet kodu: <strong>{mySeats.size}/{invitationCode.maxTickets}</strong> koltuk
              </div>
            )}

            <div className="selected-seats-sidebar">
              {Object.values(selectedSeatsWithTypes).map(seat => {
                const ticketType = ticketTypes.find(t => t.value === seat.ticketType);
                return (
                  <div key={seat.seatId} className="selected-seat-card">
                    <div className="seat-header">
                      <span className="seat-id">{seat.seatId}</span>
                      <div className="seat-details">
                        <span className="seat-section">{seat.sectionName}</span>
                        <span className="ticket-badge" style={{ background: ticketType.color }}>
                          {ticketType.label}
                        </span>
                      </div>
                    </div>
                    <div className="seat-right">
                      <span className="seat-price">₺{(seat.finalPrice ?? 0).toFixed(2)}</span>
                      <button
                        onClick={() => handleRemoveSeat(seat.seatId)}
                        className="remove-seat-btn"
                        title="Koltuk iptal"
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sidebar-total">
              <span>Toplam:</span>
              <span className="total-amount">₺{(getTotalPrice() ?? 0).toFixed(2)}</span>
            </div>

            <button onClick={handleCheckout} className="checkout-btn-sidebar">
              <i className="fa-solid fa-arrow-right" /> Devam Et ({Object.keys(selectedSeatsWithTypes).length} Bilet)
            </button>
          </div>
        )}
      </div>

      {showTicketTypeModal && (
        <div className="modal-overlay" onClick={() => setShowTicketTypeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Bilet Tipi Seçin</h3>
            <p className="modal-subtitle">
              Koltuk: {currentSeatId}
              {currentPairSeatId && ` + ${currentPairSeatId}`}
            </p>
            
            <div className="ticket-type-options">
              {ticketTypes.map(type => (
                <button
                  key={type.value}
                  className="ticket-type-option"
                  style={{ borderColor: type.color }}
                  onClick={() => handleTicketTypeSelect(type.value === 0 ? 'Full' : 'Student')}
                >
                  <span className="type-icon">{type.icon}</span>
                  <span className="type-label">{type.label}</span>
                </button>
              ))}
            </div>

            <button className="cancel-btn" onClick={() => {
              setShowTicketTypeModal(false);
              setCurrentSeatId(null);
              setCurrentPairSeatId(null);
            }}>
              <i className="fa-solid fa-xmark" /> İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketingSeatMap;